import { callable } from './callable'
import type { NlsKey } from '../nls/strings_nls'

export interface CreateAccountTransferResult {
  code: string
  expiresAtMs: number
  requiresEmailConfirmation: boolean
}

export interface ConfirmAccountTransferInput {
  code: string
  confirmToken: string
}

export interface ConfirmAccountTransferResult {
  ok: boolean
}

export interface ClaimAccountTransferInput {
  code: string
}

export interface ClaimAccountTransferResult {
  ok: boolean
  workspacesTransferred: number
}

export interface CapWorkspacesDetails {
  cap: number
  projectedCount: number
  workspaces: Array<{ workspaceId: string; name: string }>
}

export interface MappedAccountTransferError {
  nlsKey: NlsKey
  message: string
  details?: CapWorkspacesDetails
}

export async function createAccountTransferCode(): Promise<CreateAccountTransferResult> {
  return await callable<Record<string, never>, CreateAccountTransferResult>('createAccountTransferCode', {})
}

export async function confirmAccountTransfer(
  input: ConfirmAccountTransferInput,
): Promise<ConfirmAccountTransferResult> {
  return await callable<ConfirmAccountTransferInput, ConfirmAccountTransferResult>('confirmAccountTransfer', input)
}

export async function claimAccountTransfer(
  input: ClaimAccountTransferInput,
): Promise<ClaimAccountTransferResult> {
  return await callable<ClaimAccountTransferInput, ClaimAccountTransferResult>('claimAccountTransfer', input)
}

export function mapAccountTransferError(err: unknown): MappedAccountTransferError {
  const errObj = typeof err === 'object' && err !== null ? (err as Record<string, unknown>) : undefined
  const rawMsg = errObj && 'message' in errObj && typeof errObj.message === 'string' ? errObj.message : ''
  const rawCode = errObj && 'code' in errObj && typeof errObj.code === 'string' ? errObj.code : ''
  const details = errObj && 'details' in errObj ? (errObj.details as Record<string, unknown> | undefined) : undefined

  let nlsKey: NlsKey = 'NLSAccountTransferGenericError'

  if (rawMsg.includes('Anonymous accounts cannot create or claim') || rawCode === 'unauthenticated') {
    nlsKey = 'NLSAccountTransferAnonymousNotAllowed'
  } else if (rawMsg.includes('The transfer code is invalid') || rawCode === 'not-found') {
    nlsKey = 'NLSAccountTransferCodeInvalid'
  } else if (rawMsg.includes('This transfer code has already been used')) {
    nlsKey = 'NLSAccountTransferCodeAlreadyUsed'
  } else if (rawMsg.includes('This transfer code has already been claimed')) {
    nlsKey = 'NLSAccountTransferAlreadyClaimed'
  } else if (rawMsg.includes('The transfer code has expired')) {
    nlsKey = 'NLSAccountTransferCodeExpired'
  } else if (rawMsg.includes('Transfer of owned workspaces requires email confirmation')) {
    nlsKey = 'NLSAccountTransferEmailConfirmationRequired'
  } else if (rawMsg.includes('Transfer of owned workspaces requires an email address on file')) {
    nlsKey = 'NLSAccountTransferEmailRequired'
  } else if (rawMsg.includes('Cannot transfer account to the same user')) {
    nlsKey = 'NLSAccountTransferSelfTransfer'
  } else if (rawMsg.includes('Invalid confirmation token') || rawCode === 'permission-denied') {
    nlsKey = 'NLSAccountTransferInvalidConfirmToken'
  } else if (rawMsg.includes('Confirmation token is required')) {
    nlsKey = 'NLSAccountTransferTokenRequired'
  } else if (rawMsg.includes('20-workspace') || rawCode === 'resource-exhausted') {
    nlsKey = 'NLSAccountTransferExceedsCap'
  }

  let capDetails: CapWorkspacesDetails | undefined
  if (details && typeof details === 'object') {
    const d = details as Record<string, unknown>
    if (typeof d.cap === 'number' && typeof d.projectedCount === 'number' && Array.isArray(d.workspaces)) {
      capDetails = {
        cap: d.cap,
        projectedCount: d.projectedCount,
        workspaces: d.workspaces.map((w: Record<string, unknown>) => ({
          workspaceId: String(w.workspaceId ?? ''),
          name: String(w.name ?? ''),
        })),
      }
    }
  }

  return {
    nlsKey,
    message: rawMsg || 'Account transfer failed.',
    details: capDetails,
  }
}
